import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableContainer, TablePagination, TextField, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase'; // Adjust path as needed
import { textFieldStyles } from '../../Login/LoginPageStyles';

const TimesheetDialog = ({ open, onClose, taskId }) => {
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState({}); // State for controlling loading of each save button

  useEffect(() => {
    if (taskId) {
      const fetchTimesheetEntries = async () => {
        try {
          const timesheetCollection = collection(db, 'tasksTimesheet');
          const timesheetQuery = query(timesheetCollection, where('taskId', '==', taskId));
          const timesheetSnapshot = await getDocs(timesheetQuery);
          const entries = timesheetSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
          setTimesheetEntries(entries);
        } catch (error) {
          console.error('Error fetching timesheet entries: ', error);
        }
      };

      fetchTimesheetEntries();
    }
  }, [taskId, open]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCommentChange = (id, event) => {
    const updatedEntries = timesheetEntries.map((entry) => 
      entry.id === id ? { ...entry, comment: event.target.value } : entry
    );
    setTimesheetEntries(updatedEntries);
  };

  const handleSaveComment = async (entry) => {
    if (entry.comment) {
      setLoading((prev) => ({ ...prev, [entry.id]: true })); // Set loading to true for the specific entry
      try {
        const entryDocRef = doc(db, 'tasksTimesheet', entry.id);
        await updateDoc(entryDocRef, { comment: entry.comment });
        setSnackbarOpen(true); // Show Snackbar on successful save
      } catch (error) {
        console.error('Error saving comment: ', error);
      } finally {
        setLoading((prev) => ({ ...prev, [entry.id]: false })); // Set loading to false after the operation
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const paginatedEntries = timesheetEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Timesheet for Task ID: {taskId}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} elevation={0} sx={{ marginTop: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Start Time</b></TableCell>
                  <TableCell><b>End Time</b></TableCell>
                  <TableCell><b>Work Done</b></TableCell>
                  <TableCell><b>Comment</b></TableCell>
                  <TableCell></TableCell> {/* Empty cell for save button */}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntries
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((entry, index) => (
                    <TableRow
                      key={entry.id}
                      sx={{ backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#ffffff' }}
                    >
                      <TableCell>{entry.date}</TableCell>
                      <TableCell>{entry.startTime}</TableCell>
                      <TableCell>{entry.endTime}</TableCell>
                      <TableCell sx={{width: 200}}>{entry.workDone}</TableCell>
                      <TableCell sx={{width: 300}}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          multiline
                          value={entry.comment || ''}
                          onChange={(event) => handleCommentChange(entry.id, event)}
                          placeholder="Add comment"
                          sx={{ ...textFieldStyles }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
                          onClick={() => handleSaveComment(entry)}
                          disabled={loading[entry.id]} // Disable the button when loading
                        >
                          {loading[entry.id] ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={timesheetEntries.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Comment saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default TimesheetDialog;
